import React, { useRef, useState } from "react";
import { Card, Select } from "./ui";
import { runImport } from "../api";
import { money0, num } from "../lib/format";

/**
 * Upload -> preview -> commit. The server never writes on the preview call,
 * so the admin always sees the parsed rows before the table is replaced.
 */
/** One preview cell, rendered per the type the mapper declared. */
function Cell({ col, value }) {
  if (value === null || value === undefined || value === "") {
    return <td className={col.type === "money" ? "num" : ""}><span className="dash">—</span></td>;
  }
  if (col.type === "badge") {
    return <td><span className="badge">{String(value).toUpperCase()}</span></td>;
  }
  if (col.type === "status") {
    return (
      <td>
        <span className={`badge ${value === "Active" ? "badge--pos" : "badge--warn"}`}>{value}</span>
      </td>
    );
  }
  if (col.type === "money") {
    return <td className={`num ${num(value) < 0 ? "neg" : ""}`}>{money0(value)}</td>;
  }
  return <td className="truncate" title={String(value)}>{String(value)}</td>;
}

export default function ImportPanel({ importKey, label, table, mode, scope }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [sheet, setSheet] = useState("");
  /* Add-only: insert everything, delete nothing. For a month that arrived in
     two halves, where the normal replace would wipe the half already loaded. */
  const [append, setAppend] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setError(""); setSheet("");
    setAppend(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const choose = (f) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) {
      setError("Only .xlsx files are accepted.");
      return;
    }
    setFile(f); setPreview(null); setResult(null); setError(""); setSheet("");
  };

  const doParse = async (pick) => {
    if (!file) return;
    setBusy(true); setError(""); setResult(null);
    try {
      setPreview(await runImport(importKey, file, { commit: false, sheet: pick || sheet, append }));
      if (pick) setSheet(pick);
    } catch (e) {
      setError(e.message); setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const doCommit = async () => {
    if (!file || !preview) return;
    const n = preview.stats.kept;
    const removing = preview.rowsToRemove ?? 0;
    const scoped = mode === "replace_scope";
    if (!window.confirm(
      scoped
        ? `Replace ${removing} existing row${removing === 1 ? "" : "s"} in "${table}" `
          + `with ${n} from this file?\n\n`
          + `Only the ${preview.scopeCombos} month × manager slots in this file are touched. `
          + `Everything else is left alone. This cannot be undone.`
        : `Replace all ${removing} row${removing === 1 ? "" : "s"} in "${table}" `
          + `with these ${n}?\n\nThis cannot be undone. `
          + `Notes and reallocations are carried across.`
    )) return;

    setBusy(true); setError("");
    try {
      const res = await runImport(importKey, file, { commit: true, sheet, append });
      setResult(res); setPreview(null); setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const stats = preview?.stats;
  const cols =
    stats?.display ||
    (preview?.preview?.length
      ? Object.keys(preview.preview[0]).map((k) => ({ key: k, label: k, type: "text" }))
      : []);

  return (
    <Card
      title={label}
      note={
        append
          ? `add only — inserts into ${table} and deletes nothing`
          : mode === "replace"
            ? `replaces every row in ${table}`
            : mode === "replace_scope"
              ? `replaces only the ${scope || "months"} present in the file`
              : mode === "upsert"
                ? `updates ${table} in place, never replaces it`
                : table
      }
      actions={(file || preview || result) && (
        <button className="btn btn--sm btn--ghost" onClick={reset} disabled={busy}>Start over</button>
      )}
    >
      {(mode === "replace" || mode === "replace_scope") && (
        <label className="append-toggle">
          <input
            type="checkbox"
            checked={append}
            disabled={busy || !!result}
            onChange={(e) => { setAppend(e.target.checked); setPreview(null); }}
          />
          <span>
            <strong>Add only</strong> — insert these rows and delete nothing.
            Use it when a month arrived in two files: a normal import replaces
            the whole month and would wipe the half already loaded. The preview
            says how much of the file already looks present, but importing the
            same file twice will double it.
          </span>
        </label>
      )}

      <div
        className={`drop${dragging ? " is-over" : ""}${file ? " has-file" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); choose(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => choose(e.target.files?.[0])}
        />
        {file ? (
          <>
            <strong>{file.name}</strong>
            <span className="muted">{(file.size / 1024).toFixed(0)} KB · click to choose a different file</span>
          </>
        ) : (
          <>
            <strong>Drop the .xlsx here</strong>
            <span className="muted">or click to browse</span>
          </>
        )}
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn" onClick={() => doParse()} disabled={!file || busy}>
          {busy && !preview ? "Reading…" : "Parse & preview"}
        </button>
        {preview && (
          <button className="btn btn--primary" onClick={doCommit} disabled={busy}>
            {busy ? "Importing…" : `Import ${stats.kept} row${stats.kept === 1 ? "" : "s"}`}
          </button>
        )}
      </div>

      {error && <div className="notice notice--warn import-msg">{error}</div>}

      {result?.committed && (
        <div className="notice import-msg">
          Imported <strong>{result.rowsInserted ?? result.rowsAfter}</strong> rows
          {" "}from {result.filename}
          {result.rowsRemoved != null && `, replacing ${result.rowsRemoved}`}.
          {" "}Table now holds {result.rowsAfter} (was {result.rowsBefore}).
          {result.created && Object.entries(result.created).map(([t, n]) => ` ${n} new ${t} row(s) created.`)}
          {result.notesCarriedOver > 0 &&
            ` ${result.notesCarriedOver} existing note${result.notesCarriedOver === 1 ? "" : "s"} carried over.`}
        </div>
      )}

      {stats?.sheets?.length > 1 && (
        <div className="row" style={{ marginTop: 12, gap: 8 }}>
          <span className="field-label">Sheet</span>
          <Select
            value={stats.sheet}
            onChange={(v) => doParse(v)}
            options={stats.sheets.map((sh) => ({ value: sh, label: sh }))}
            style={{ minWidth: 240 }}
          />
          <span className="toolbar-note">
            this workbook has {stats.sheets.length} year tabs — pick the one to import
          </span>
        </div>
      )}

      {stats && (
        <div className="import-result">
          <div className="stat-grid" style={{ marginBottom: 12 }}>
            <div className="stat" style={{ "--stat-accent": "var(--info)" }}>
              <div className="stat-label">Rows in file</div>
              <div className="stat-value-row"><span className="stat-value stat-value--plain">{stats.parsed}</span></div>
            </div>
            <div className="stat" style={{ "--stat-accent": "var(--accent)" }}>
              <div className="stat-label">Will be imported</div>
              <div className="stat-value-row"><span className="stat-value">{stats.kept}</span></div>
            </div>
            {preview.appendOnly && (
              <div className="stat" style={{ "--stat-accent": "var(--warn)" }}>
                <div className="stat-label">Already look present</div>
                <div className="stat-value-row">
                  <span
                    className="stat-value"
                    style={{ color: preview.alreadyPresent ? "var(--warn)" : "var(--pos)" }}
                  >
                    {preview.alreadyPresent ?? 0}
                  </span>
                </div>
                <div className="stat-meta">
                  {preview.alreadyPresent
                    ? "same month, description and amount — check before adding"
                    : "nothing in the file matches what is already there"}
                </div>
              </div>
            )}
            {!preview.appendOnly && preview.rowsToRemove != null && (
              <div className="stat" style={{ "--stat-accent": "var(--neg)" }}>
                <div className="stat-label">Existing rows replaced</div>
                <div className="stat-value-row">
                  <span className="stat-value" style={{ color: "var(--neg)" }}>
                    {preview.rowsToRemove}
                  </span>
                </div>
                {preview.scopeCombos != null && (
                  <div className="stat-meta">
                    across {preview.scopeCombos} {scope || "months"}
                  </div>
                )}
              </div>
            )}
            {stats.headline && (
              <div className="stat" style={{ "--stat-accent": "var(--warn)" }}>
                <div className="stat-label">{stats.headline.label}</div>
                <div className="stat-value-row">
                  <span className="stat-value stat-value--plain">
                    {stats.headline.format === "money"
                      ? money0(stats.headline.value)
                      : stats.headline.value}
                  </span>
                </div>
              </div>
            )}
            <div className="stat" style={{ "--stat-accent": "var(--pos)" }}>
              <div className="stat-label">Partners</div>
              <div className="stat-value-row">
                <span className="stat-value stat-value--plain">{Object.keys(stats.by_user || {}).length}</span>
              </div>
            </div>
          </div>

          <div className="row row--wrap" style={{ marginBottom: 10 }}>
            {Object.entries(stats.by_user || {}).map(([u, n]) => (
              <span className="badge badge--accent" key={u}>{u.toUpperCase()} {n}</span>
            ))}
          </div>

          {preview.willCreate && Object.entries(preview.willCreate).map(([table, items]) => (
            <div className="notice import-msg" key={table}>
              {items.length} new {table === "accounts" ? "account" : "project"}
              {items.length === 1 ? "" : "s"} will be created:{" "}
              {items.slice(0, 6).join(", ")}{items.length > 6 ? "…" : ""}
              {table === "accounts" && " — number only, name left blank for the accounts import to fill in."}
            </div>
          ))}

          {(stats.warnings || []).map((w, i) => (
            <div className="notice notice--warn import-msg" key={i}>{w}</div>
          ))}

          {/*
            * Rows the mapper left out, named.
            *
            * A count on its own is no help: "1 row dropped, zero amount" is not
            * something anyone connects to a missing credit note, and one did go
            * missing that way for three years. So every dropped row is listed
            * with enough of the sheet to find it again.
            */}
          {!!(stats.droppedRows || []).length && (
            <details className="import-drops">
              <summary>
                {stats.droppedRows.length} row{stats.droppedRows.length === 1 ? "" : "s"}{" "}
                left out — check these
              </summary>
              <div className="tbl-wrap" style={{ maxHeight: 260 }}>
                <table className="tbl tbl-zebra">
                  <thead>
                    <tr>
                      <th>Sheet row</th><th>Why</th><th>Team</th><th>Project</th>
                      <th>Client / invoice</th><th>Month</th><th className="num">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.droppedRows.map((d, i) => (
                      <tr key={i}>
                        <td className="muted">{d.row}</td>
                        <td>{d.reason}</td>
                        <td>{d.team || "—"}</td>
                        <td>{d.project || "—"}</td>
                        <td className="muted">
                          {[d.client, d.invoice].filter(Boolean).join(" — ") || "—"}
                        </td>
                        <td>{d.month || "—"}</td>
                        <td className="num">
                          {d.amount_ccy == null ? "—"
                            : `${d.ccy || ""} ${d.amount_ccy.toLocaleString(undefined, {
                                minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {!!(stats.recoveredRows || []).length && (
            <details className="import-drops">
              <summary>
                {stats.recoveredRows.length} row{stats.recoveredRows.length === 1 ? "" : "s"}{" "}
                taken at the invoice amount — no P&amp;L figure in the sheet
              </summary>
              <div className="tbl-wrap" style={{ maxHeight: 260 }}>
                <table className="tbl tbl-zebra">
                  <thead>
                    <tr>
                      <th>Sheet row</th><th>Team</th><th>Project</th>
                      <th>Client / invoice</th><th>Month</th><th className="num">Taken as</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recoveredRows.map((d, i) => (
                      <tr key={i}>
                        <td className="muted">{d.row}</td>
                        <td>{d.team || "—"}</td>
                        <td>{d.project || "—"}</td>
                        <td className="muted">
                          {[d.client, d.invoice].filter(Boolean).join(" — ") || "—"}
                        </td>
                        <td>{d.month || "—"}</td>
                        <td className="num">
                          {d.amount_ccy == null ? "—"
                            : d.amount_ccy.toLocaleString(undefined, {
                                minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {stats.skipped > 0 && (
            <p className="toolbar-note" style={{ marginBottom: 10 }}>
              {stats.parsed} rows in the sheet · {stats.dropped_no_partner ?? 0} were
              summary/footer lines · {stats.skipped} held back · {stats.kept} will import.
              {stats.dropped && Object.values(stats.dropped).some((n) => n > 0) && (
                <> · left out: {Object.entries(stats.dropped)
                  .filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(", ")}.</>
              )}
            </p>
          )}

          {Object.keys(stats.unmapped || {}).length > 0 && (
            <div className="notice notice--warn import-msg">
              No login mapped for: {Object.entries(stats.unmapped).map(([o, n]) => `${o} (${n})`).join(", ")}.
              These rows still import, but under an unrecognised username — only fd will see them.
            </div>
          )}

          {stats.by_period && (
            <div className="scope-grid">
              <span className="field-label">Months in this file</span>
              <div className="row row--wrap" style={{ marginTop: 6 }}>
                {Object.entries(stats.by_period).map(([p, n]) => (
                  <span className="badge" key={p}>{p} <em className="muted2">{n}</em></span>
                ))}
              </div>
            </div>
          )}

          <div className="tbl-wrap" style={{ maxHeight: 340 }}>
            <table className="tbl tbl-zebra">
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c.key}
                        className={[c.type === "money" ? "num" : "", c.flex ? "col-flex" : ""].join(" ").trim()}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((r, i) => (
                  <tr key={i}>
                    {cols.map((c) => <Cell key={c.key} col={c} value={r[c.key]} />)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.previewTruncated && (
            <p className="toolbar-note" style={{ marginTop: 8 }}>
              Showing the first 100 rows — all {stats.kept} will be imported.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
